import { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import { useSelector } from "react-redux"
import { Box, Center, Group, UnstyledButton } from "@mantine/core"
import { useDebouncedState } from "@mantine/hooks"
import { IconEdit, IconUserCircle } from "@tabler/icons-react"

import { Button, EmptyMessage, Loader, Modal, Text, TextInput } from "components"
import { AppConstants, ServiceConstants } from "constant"
import { useListHook } from "hooks"
import { ChatService, UserService } from "services"
import { ChatResponse, UserResponse } from "types"
import { AppUtils, StorageUtils } from "utils"

import { IRootState } from "config/redux"

const { STORAGE_USER } = AppConstants;
const { REQUEST_PENDING, REQUEST_USERS_CLIENTS_MY_ID, REQUEST_CHATS_ONE_ID } = ServiceConstants;

interface ChatUserNewProps {
  onSelect: (chat: ChatResponse) => void;
}

const ChatUserNew = ({ onSelect }: ChatUserNewProps) => {
  const { t } = useTranslation();
  const serviceObject = useSelector((state: IRootState) => state.service.object[REQUEST_USERS_CLIENTS_MY_ID]);
  const chatServiceObject = useSelector((state: IRootState) => state.service.object[REQUEST_CHATS_ONE_ID]);
  const { content, updatePageable, totalElements, resetList } = useListHook();
  const [loggedUser] = useState<UserResponse>(StorageUtils.getObject(STORAGE_USER));
  const [pageSize, setPageSize] = useState<number>(20);
  const [visible, setVisible] = useState<boolean>(false);
  const [search, setSearch] = useDebouncedState('', 500);

  useEffect(() => {
    return(() => {
      setPageSize(20);
      setSearch('');
      resetList();
    });
  }, [visible]);

  useEffect(() => {
    if (search === '') {
      resetList();
      return;
    }
    UserService.userClientsMy({ search: search, page: 0, pageSize: pageSize });
  }, [search, pageSize]);

  useEffect(() => {
    AppUtils.resolveServiceObject(serviceObject, REQUEST_USERS_CLIENTS_MY_ID, updatePageable);
    AppUtils.resolveServiceObject(chatServiceObject, REQUEST_CHATS_ONE_ID, (val) => {
        setVisible(false);
        onSelect(val);
    })
  }, [serviceObject, chatServiceObject]);

  const isLoading = useMemo(() => serviceObject && serviceObject.status === REQUEST_PENDING, [serviceObject])

  const users = useMemo(() => {
    if (!content) {
      return <></>;
    }
    return content.map((item, index) => (
      <UnstyledButton
        key={`user-${index}`}
        w={'100%'}
        h={32}
        onClick={() => ChatService.chatsOne(item.id)}
        pt={6}
      >
        <Group gap={0}>
          <IconUserCircle stroke={1} size={32} />
          <Text mb={0} mt={0} ml={'xs'}>{item.fullName}</Text>
        </Group>
      </UnstyledButton>
    ))
  }, [content]);

  if (loggedUser.profile.id !== 2) {
    return <></>
  }

  return (
    <>
      <Button variant={'transparent'} size={'compact-xs'} onClick={() => setVisible(true)}>
        <IconEdit color={'var(--mantine-color-black)'} />
      </Button>
      <Modal opened={visible} onClose={() => setVisible(false)} title={t('chat.message.new.title')} style={{ overflowY: 'auto' }}>
        <TextInput
          placeholder={t('chat.message.new.placeholder')}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus={true}
        />
        <Box mt={'md'}>
          <Box pos={'relative'}>
            <Loader overlay={true} visible={content === undefined || isLoading} />
            {content && (
              content.length === 0 ?
                <EmptyMessage message={t('chat.message.new.empty')} />
              :
              <>
                {users}
                {totalElements > content.length && pageSize !== 0 &&
                  <Center>
                    <Button
                      size={'compact-xs'}
                      variant={'transparent'}
                      c={'var(--mantine-color-primary-6)'}
                      onClick={() => setPageSize(100)}>
                        {t('chat.message.new.all')}
                    </Button>
                  </Center>
                }
              </>
            )}
          </Box>
        </Box>
      </Modal>
    </>
  )
}

export default ChatUserNew;